import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

// ---------- Brands managed by the agency ----------
const BRANDS = [
  "Mercato Comunale Isola",
  "Beautin' Cosmetics",
  "Farmacia Furtei",
  "Seaflower Restaurant",
  "Elèt Eventi",
  "Teravista",
];

// ---------- Case studies (Attività gestite) ----------
const cases = [
  {
    id: "seaflower",
    title: "Seaflower Restaurant",
    tag: "Ristorazione · F&B",
    cover: "/assets/seaflower-cocktail.jpg",
    gallery: [
      { type: "video", src: "/assets/seaflower-highlight.mp4", poster: "/assets/seaflower-highlight-poster.jpg", label: "Highlight reel — mood serale" },
      "/assets/seaflower-cocktail.jpg",
      "/assets/seaflower-pizza-top.jpg",
      "/assets/seaflower-pizza-side.jpg",
    ],
    desc: "Riposizionamento visivo e strategia contenuti per un ristorante-cocktail bar con vista mare: nuova brand identity fotografica, format costanti e Reels ad alto engagement.",
    services: [
      "Gestione social",
      "Produzione videoclip e foto",
      "Video drone",
      "Shooting prodotti menù",
      "Advertising",
    ],
    tid: "case-seaflower",
  },
  {
    id: "teravista",
    title: "Teravista — Aerial Photography",
    tag: "Servizi · Riprese aeree",
    cover: "/assets/case-drone.jpg",
    gallery: ["/assets/case-drone.jpg"],
    desc: "Lancio digitale di uno studio di riprese aeree con drone: strategia editoriale, contenuti verticali e ADV Meta targettizzate su wedding planner, resort e imprese edili.",
    services: [
      "Gestione social",
      "Advertising",
    ],
    tid: "case-teravista",
  },
  {
    id: "beautin",
    title: "Beautin' Cosmetics",
    tag: "Beauty",
    cover: "/assets/beautin-woman.jpg",
    gallery: [
      "/assets/beautin-woman.jpg",
      "/assets/beautin-all-products.webp",
      "/assets/beautin-blush-high.webp",
      "/assets/beautin-highlighter.webp",
      "/assets/beautin-blush-rosy.webp",
      "/assets/beautin-blush-blushin.webp",
    ],
    galleries: [
      {
        id: "e-commerce",
        label: "E-commerce",
        subtitle: "Shooting prodotto — packaging & line-up",
        photos: [
          { src: "/assets/beautin-all-products.webp", label: "Beautin' Cosmetics — line-up completa di blush e highlighter (shooting still-life e-commerce)" },
          { src: "/assets/beautin-blush-high.webp", label: "Beautin' Cosmetics — blush liquido e highlighter in formazione prodotto (packaging shot)" },
          { src: "/assets/beautin-highlighter.webp", label: "Beautin' Cosmetics — liquid highlighter champagne, dettaglio prodotto per e-commerce" },
          { src: "/assets/beautin-blush-rosy.webp", label: "Beautin' Cosmetics — blush 01 Rosy Cheeks, still life su sfondo neutro" },
          { src: "/assets/beautin-blush-blushin.webp", label: "Beautin' Cosmetics — blush 02 Blushin', packaging shot con lighting morbido" },
        ],
      },
      {
        id: "aesthetic",
        label: "Aesthetic",
        subtitle: "Editoriali e mood estivi",
        photos: [
          { src: "/assets/beautin-aesthetic-1.webp", label: "Shooting editoriale Beautin' Cosmetics — applicazione blush con vista mare, mood estivo" },
          { src: "/assets/beautin-aesthetic-2.webp", label: "Beautin' Cosmetics — modelle poolside, campagna magazine beauty estiva" },
          { src: "/assets/beautin-aesthetic-3.webp", label: "Beautin' Cosmetics — editoriale girls edit su letto a baldacchino, atmosfera romantica" },
          { src: "/assets/beautin-aesthetic-4.webp", label: "Beautin' Cosmetics — ritratto in giardino, aesthetic naturale e luminoso" },
          { src: "/assets/beautin-aesthetic-5.webp", label: "Beautin' Cosmetics — Skin Detox Tonic, scatto still-life con elementi botanici" },
          { src: "/assets/beautin-aesthetic-6.webp", label: "Beautin' Cosmetics — beauty poolside con serum, lifestyle solare estivo" },
          { src: "/assets/beautin-aesthetic-7.webp", label: "Beautin' Cosmetics — Nutri Cream nel giardino di ortensie, mood romantico" },
        ],
      },
      {
        id: "glamour",
        label: "Glamour",
        subtitle: "Beauty portrait e ritratto d'autore",
        photos: [
          { src: "/assets/beautin-glamour-1.webp", label: "Beautin' Cosmetics — glamour portrait 01, serata evento beauty" },
          { src: "/assets/beautin-glamour-2.webp", label: "Beautin' Cosmetics — glamour portrait 02, backstage party esclusivo" },
          { src: "/assets/beautin-glamour-3.webp", label: "Beautin' Cosmetics — glamour portrait 03, atmosfera notturna e luci neon" },
          { src: "/assets/beautin-glamour-4.webp", label: "Beautin' Cosmetics — glamour portrait 04, close-up ritratto d'autore" },
          { src: "/assets/beautin-glamour-5.webp", label: "Beautin' Cosmetics — glamour portrait 05, campagna lifestyle serale" },
          { src: "/assets/beautin-glamour-6.webp", label: "Beautin' Cosmetics — glamour portrait 06, evento aziendale beauty brand" },
          { src: "/assets/beautin-glamour-7.webp", label: "Beautin' Cosmetics — glamour portrait 07, scatto editoriale notturno" },
          { src: "/assets/beautin-glamour-8.webp", label: "Beautin' Cosmetics — glamour portrait 08, finale campagna launch" },
        ],
      },
    ],
    desc: "Content creation e storytelling di prodotto per un brand beauty premium: shooting editoriali, reel sensoriali e piano ADV per e-commerce con focus su liquid highlighter e blush.",
    services: [
      "Gestione social",
      "Produzione videoclip e foto",
      "Shooting prodotti e-commerce",
      "Gestione e-commerce",
      "Advertising",
    ],
    tid: "case-beautin",
  },
  {
    id: "mercato-comunale-isola",
    title: "Mercato Comunale Isola",
    tag: "Retail · Mercato",
    cover: "/assets/isola-1.jpg",
    gallery: [
      { src: "/assets/isola-1.jpg", label: "Mercato Comunale Isola — facciata esterna, quartiere Isola Milano" },
      { src: "/assets/isola-2.jpg", label: "Mercato Comunale Isola — corridoio centrale tra i banchi degli operatori" },
      { src: "/assets/isola-3.webp", label: "Mercato Comunale Isola — dehors esterno con tavoli e sedute" },
    ],
    desc: "Progetto in arrivo: gestione della comunicazione digitale del Mercato Comunale Isola con contenuti dedicati agli operatori, storytelling di quartiere e campagne per gli eventi in programma.",
    services: [
      "Gestione social",
      "Contenuti di quartiere",
      "Advertising eventi",
      "Storytelling operatori",
    ],
    tid: "case-mercato-comunale-isola",
    upcoming: true,
  },
];

// ---------- Photo gallery categories (kept inside case modals now) ----------
// (Old outer categories removed — galleries are declared per-case in `cases[].galleries`)

const metrics = [
  { end: 45, suffix: "M+", label: "Views generate" },
  { end: 89, suffix: "K+", label: "Community follower" },
  { end: 217, suffix: "K+", label: "Reazioni & interazioni" },
  { end: 509, suffix: "%", label: "Crescita audience annua" },
];

// Advertising performance (Meta / Google / TikTok Ads)
const adv = [
  { end: 150, suffix: "K €", prefix: "+", label: "Budget ADV gestito" },
  { end: 186, suffix: "M+", label: "Impressioni ADV" },
  { end: 6, suffix: "M+", label: "Video views ADV" },
  { end: 96, suffix: "K+", label: "Click generati" },
];

function Counter({ end, suffix, prefix, size = "lg" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const duration = 1600;
        const start = performance.now();
        const isFloat = end % 1 !== 0;
        const step = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          const v = end * eased;
          setN(isFloat ? Number(v.toFixed(1)) : Math.round(v));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        obs.disconnect();
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end]);
  const sizeCls = size === "md" ? "text-3xl md:text-4xl" : "text-5xl md:text-6xl";
  return (
    <span ref={ref} className={`font-display text-white whitespace-nowrap ${sizeCls}`}>
      {prefix}{n}
      <span className="text-gradient">{suffix}</span>
    </span>
  );
}

function CaseModal({ item, onClose }) {
  const [idx, setIdx] = useState(0);
  const [activeCat, setActiveCat] = useState(() => item.galleries?.[0]?.id || null);
  const [lightbox, setLightbox] = useState(null); // { catId, idx }
  const hasMultiGalleries = Array.isArray(item.galleries) && item.galleries.length > 0;
  const hasGallery = !hasMultiGalleries && item.gallery && item.gallery.length > 0;
  const currentCat = hasMultiGalleries ? item.galleries.find((c) => c.id === activeCat) : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightbox) setLightbox(null);
        else onClose();
        return;
      }
      if (!hasGallery) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % item.gallery.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + item.gallery.length) % item.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [hasGallery, item.gallery, onClose, lightbox]);

  const containerClass = hasMultiGalleries
    ? "glass gradient-border rounded-3xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col"
    : "glass gradient-border rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid={`case-modal-${item.id}`}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className={containerClass}
      >
        {hasMultiGalleries ? (
          // Bigger modal with tabbed galleries (unchanged)
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-start justify-between gap-4 p-6 md:p-8 pb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-purple-300/80">{item.tag}</div>
                <h3 className="font-display text-2xl md:text-3xl text-white mt-1 leading-tight">{item.title}</h3>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-3xl">{item.desc}</p>
              </div>
              <button onClick={onClose} aria-label="Chiudi" data-testid={`case-modal-close-${item.id}`} className="shrink-0 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/5">
                <X size={20} />
              </button>
            </div>

            <div className="px-6 md:px-8">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Servizi erogati</div>
              <ul className="flex flex-wrap gap-2 mb-6" data-testid={`case-modal-services-${item.id}`}>
                {item.services.map((r) => (
                  <li key={r} className="text-xs text-slate-100 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            <div className="px-6 md:px-8 flex flex-wrap gap-2 mb-4">
              {item.galleries.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCat(c.id)}
                  data-testid={`gallery-tab-${c.id}`}
                  className={`px-4 py-2 rounded-full text-xs font-medium border transition ${
                    activeCat === c.id
                      ? "bg-gradient-to-r from-purple-500 to-blue-500 border-transparent text-white shadow-[0_0_20px_rgba(147,51,234,0.35)]"
                      : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25"
                  }`}
                >{c.label}</button>
              ))}
            </div>

            {currentCat?.subtitle && (
              <div className="px-6 md:px-8 text-sm text-slate-400 mb-4">{currentCat.subtitle}</div>
            )}

            <div className="px-6 md:px-8 pb-8 overflow-y-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCat}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {currentCat && currentCat.photos.length > 0 ? (
                    <div
                      className="grid gap-3 md:gap-4"
                      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(200px, 100%), 1fr))" }}
                      data-testid={`gallery-grid-${currentCat.id}`}
                    >
                      {currentCat.photos.map((ph, i) => (
                        <button
                          type="button"
                          key={ph.src}
                          onClick={() => setLightbox({ catId: currentCat.id, idx: i })}
                          data-testid={`gallery-photo-${currentCat.id}-${i}`}
                          className="relative group rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-white/25 transition aspect-[4/5] focus-visible:outline-none"
                        >
                          <img
                            src={ph.src}
                            alt={ph.label}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                          />
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                          <div className="pointer-events-none absolute bottom-3 left-3 right-3 text-white text-xs opacity-0 group-hover:opacity-100 transition">{ph.label}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div
                      data-testid={`gallery-empty-${currentCat?.id}`}
                      className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] py-14 flex flex-col items-center justify-center text-center gap-2"
                    >
                      <Camera size={22} className="text-purple-300" />
                      <div className="text-slate-200 font-display text-lg">Foto in arrivo</div>
                      <div className="text-slate-500 text-xs max-w-sm px-6">
                        Stiamo caricando i prossimi shooting della categoria <span className="text-slate-300">{currentCat?.label}</span>. Torna presto.
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {lightbox && (
                <GalleryLightbox
                  category={item.galleries.find((c) => c.id === lightbox.catId)}
                  startIdx={lightbox.idx}
                  onClose={() => setLightbox(null)}
                />
              )}
            </AnimatePresence>
          </div>
        ) : (
          // Classic modal (single-image slider)
          <>
            <div className="relative md:w-3/5 bg-black min-h-[240px]">
              {hasGallery ? (
                <>
                  {(() => {
                    const g = item.gallery[idx];
                    if (g && typeof g === "object" && g.type === "video") {
                      return (
                        <video
                          key={g.src}
                          src={g.src}
                          poster={g.poster}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full h-64 md:h-full object-contain bg-black"
                          data-testid={`case-modal-video-${item.id}`}
                        />
                      );
                    }
                    return (
                      <img
                        src={typeof g === "string" ? g : g?.src}
                        alt={item.title}
                        className="w-full h-64 md:h-full object-cover"
                      />
                    );
                  })()}
                  {item.gallery.length > 1 && (
                    <>
                      <button
                        onClick={() => setIdx((i) => (i - 1 + item.gallery.length) % item.gallery.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/60 border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition"
                        aria-label="Precedente" data-testid={`case-modal-prev-${item.id}`}
                      ><ChevronLeft size={16} /></button>
                      <button
                        onClick={() => setIdx((i) => (i + 1) % item.gallery.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/60 border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition"
                        aria-label="Successiva" data-testid={`case-modal-next-${item.id}`}
                      ><ChevronRight size={16} /></button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {item.gallery.map((_, i) => (
                          <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-64 md:h-full flex flex-col items-center justify-center text-center gap-3 bg-gradient-to-br from-purple-500/25 to-blue-500/15 px-8">
                  <Camera size={26} className="text-purple-300" />
                  <div className="text-slate-200 text-sm">Immagini in arrivo</div>
                  <div className="text-slate-400 text-xs">Case study in fase di lancio</div>
                </div>
              )}
            </div>
            <div className="md:w-2/5 p-6 md:p-8 overflow-y-auto">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-purple-300/80">{item.tag}</div>
                  <h3 className="font-display text-2xl md:text-3xl text-white mt-1 leading-tight">{item.title}</h3>
                </div>
                <button onClick={onClose} aria-label="Chiudi" data-testid={`case-modal-close-${item.id}`} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/5">
                  <X size={20} />
                </button>
              </div>
              <p className="mt-5 text-sm text-slate-300 leading-relaxed">{item.desc}</p>
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Servizi erogati</div>
                <ul className="space-y-2" data-testid={`case-modal-services-${item.id}`}>
                  {item.services.map((r) => (
                    <li key={r} className="text-sm text-slate-100 flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gradient-to-r from-purple-400 to-blue-400" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function GalleryLightbox({ category, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx || 0);
  const photos = category?.photos || [];
  useEffect(() => {
    if (photos.length === 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [photos.length, onClose]);
  if (!category) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose} data-testid={`gallery-lightbox-${category.id}`}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl w-full"
      >
        <button
          onClick={onClose}
          aria-label="Chiudi"
          data-testid={`gallery-close-${category.id}`}
          className="absolute -top-2 -right-2 md:top-3 md:right-3 z-10 h-9 w-9 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-black/90 transition"
        ><X size={16} /></button>
        <div className="glass gradient-border rounded-3xl overflow-hidden">
          <div className="relative aspect-[16/10] bg-black">
            <img src={photos[idx]?.src} alt={photos[idx]?.label} className="absolute inset-0 h-full w-full object-contain bg-white/[0.02]" />
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-black/90 transition"
                  aria-label="Precedente"
                ><ChevronLeft size={18} /></button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % photos.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-black/90 transition"
                  aria-label="Successiva"
                ><ChevronRight size={18} /></button>
              </>
            )}
          </div>
          <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-purple-300/80">{category.label}</div>
              <div className="text-white text-sm mt-1">{photos[idx]?.label}</div>
            </div>
            <div className="text-slate-400 text-xs tabular-nums">{idx + 1} / {photos.length}</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Clienti() {
  const [openCase, setOpenCase] = useState(null);
  const loop = [...BRANDS, ...BRANDS];

  return (
    <section id="clienti" data-testid="section-clienti" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-purple-300/80 mb-4">04 · Clienti</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
            Le storie che <br />aiutiamo a <span className="text-gradient">emergere</span>.
          </h2>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
          {metrics.map((m) => (
            <div key={m.label} className="glass gradient-border rounded-3xl p-8" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Counter end={m.end} suffix={m.suffix} />
              <div className="mt-3 text-xs uppercase tracking-widest text-slate-400">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Advertising performance callout */}
        <div className="relative rounded-3xl overflow-hidden mb-16 border border-white/10" data-testid="adv-performance">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/15 via-blue-600/10 to-purple-900/20" />
          <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-purple-500/25 blur-[80px]" />
          <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-blue-500/25 blur-[80px]" />
          <div className="relative p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-purple-200/90 mb-3 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Advertising performance
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight max-w-2xl">
                  Campagne <span className="text-gradient">Meta, Google & TikTok Ads</span> gestite in prima persona.
                </h3>
                <p className="mt-3 text-slate-400 text-sm max-w-2xl">
                  Dati aggregati sui budget pianificati e ottimizzati dal team negli ultimi 12 mesi tra Meta Ads, Google Ads e TikTok Ads.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-3 text-slate-500 text-xs">
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">Meta Ads</span>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">Google Ads</span>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">TikTok Ads</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {adv.map((m) => (
                <div
                  key={m.label}
                  data-testid={`adv-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm p-5 md:p-6"
                >
                  <Counter end={m.end} suffix={m.suffix} prefix={m.prefix} size="md" />
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Brand marquee */}
        <div className="relative overflow-hidden py-8 border-y border-white/10 mb-16" data-testid="clients-marquee">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#05050A] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#05050A] to-transparent z-10" />
          <div className="marquee-track gap-16 px-8">
            {loop.map((b, i) => (
              <div key={i} data-testid={`brand-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`} className="shrink-0 font-display text-2xl md:text-3xl text-slate-400/80 hover:text-white transition-colors whitespace-nowrap">
                {b}
              </div>
            ))}
          </div>
        </div>

        {/* Big Attività Gestite panel */}
        <div className="glass gradient-border rounded-[2rem] p-6 md:p-12" data-testid="attivita-gestite-panel">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-purple-300/80 mb-2">Attività gestite</div>
              <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">Progetti e brand seguiti dal team</h3>
              <p className="mt-3 text-slate-400 text-sm md:text-base max-w-2xl">
                Clicca su un cliente per vedere il progetto e la gallery dei shooting selezionati.
              </p>
            </div>
            <div className="hidden md:block text-slate-500 text-sm">Clicca per il dettaglio</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {cases.map((c, i) => (
              <motion.button
                type="button"
                key={c.id}
                data-testid={c.tid}
                onClick={() => setOpenCase(c)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="text-left glass gradient-border rounded-3xl overflow-hidden group focus-visible:outline-none"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {c.cover ? (
                    <img
                      src={c.cover}
                      alt={c.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.08]"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-purple-500/25 to-blue-500/15 text-center px-4 gap-2">
                      <Camera size={22} className="text-purple-200" />
                      <div className="text-xs uppercase tracking-widest text-purple-200/80">Prossimamente</div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/25 via-transparent to-blue-900/20 mix-blend-overlay" />
                  <div className="absolute top-4 left-4 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3 py-1 text-[10px] tracking-widest uppercase text-white/90">
                    {c.tag}
                  </div>
                  {c.upcoming && (
                    <div className="absolute top-4 right-4 rounded-full border border-purple-300/40 bg-purple-500/25 backdrop-blur px-3 py-1 text-[10px] tracking-widest uppercase text-white">
                      Nuovo
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-7">
                  <h4 className="font-display text-lg md:text-xl text-white leading-tight">{c.title}</h4>
                  <p className="mt-3 text-slate-400 text-sm leading-relaxed line-clamp-2">{c.desc}</p>
                  <div className="mt-5 flex items-center gap-2 text-purple-300 text-xs uppercase tracking-widest">
                    Vedi risultati <ExternalLink size={12} />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Attività gestite panel content ends here — gallery is now inside each case modal */}
        </div>
      </div>

      <AnimatePresence>
        {openCase && <CaseModal item={openCase} onClose={() => setOpenCase(null)} />}
      </AnimatePresence>
    </section>
  );
}
